import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/** How long to wait for the target to appear (a lazy page loading), in ms. */
export const WAIT_MS = 3000;
/** When to scroll again, once the page's 0.4s enter (PageTransition's 20px
 *  slide) has settled and moved the target. */
export const SETTLE_MS = 500;

const CANCEL_ON = ["wheel", "touchstart", "keydown", "mousedown"];

/**
 * Scrolls to the element a URL's #hash names, whenever the path or the hash
 * changes: `/features#how-it-works` from another page, the old How it works
 * redirect, a cold load, or a link to a section of the page already open.
 *
 * The element often isn't there yet. Pages are lazy, and App's page
 * transition only mounts the new page after the old one has left, so the
 * target is watched for with a MutationObserver and given up on after
 * WAIT_MS, silently: a stale anchor leaves the page at its top. Once found it
 * is scrolled to at once (never smoothly, so reduced motion needs no case of
 * its own), honouring its `scroll-margin-top` for the floating header, and
 * once more after SETTLE_MS, when the page's own entrance has finished
 * moving it. The moment the reader scrolls, touches, clicks or presses a key,
 * the hook stops: it never fights them. It never moves focus.
 *
 * No hash, no work: App's own page-change reset still sends a new page to its
 * top.
 */
export default function useScrollToHash() {
  // `key` too: a link to the section already in the URL (the footer's, read
  // on Features) changes neither path nor hash, only the navigation's key.
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (!hash || hash === "#") return undefined;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return undefined;
    }

    let observer = null;
    let settle = 0;
    let giveUp = 0;
    let done = false;

    const stop = () => {
      done = true;
      observer?.disconnect();
      observer = null;
      clearTimeout(settle);
      clearTimeout(giveUp);
      CANCEL_ON.forEach((type) => window.removeEventListener(type, stop));
    };

    const land = (el) => {
      observer?.disconnect();
      observer = null;
      clearTimeout(giveUp);
      el.scrollIntoView();
      settle = setTimeout(() => {
        if (done) return;
        const again = document.getElementById(id);
        if (again) again.scrollIntoView();
        stop();
      }, SETTLE_MS);
    };

    CANCEL_ON.forEach((type) => window.addEventListener(type, stop, { passive: true }));

    const found = document.getElementById(id);
    if (found) {
      land(found);
    } else {
      observer = new MutationObserver(() => {
        const el = document.getElementById(id);
        if (el) land(el);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      giveUp = setTimeout(stop, WAIT_MS);
    }

    return stop;
  }, [pathname, hash, key]);
}
